import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MsalProvider, useMsal } from '@azure/msal-react';
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser';
import { Spinner, Stack } from '../ui';
import { readAuthConfig, type AuthConfig } from './config';
import { apiScopes, toMsalConfiguration } from './msal';

/** Whether the caller is known to be signed in. `loading` covers the redirect and init round-trips. */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

/** The signed-in identity, as far as the client needs to render it. Roles are enforced server-side. */
export interface AuthUser {
  readonly name: string;
  readonly subject: string | undefined;
}

/** Options for {@link AuthContextValue.signIn}. */
export interface SignInOptions {
  /** Start Entra's hosted flow on its sign-up experience rather than sign-in. */
  readonly createAccount?: boolean;
}

/** The authentication surface consumed by the rest of the app. */
export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
  /** Begins interactive sign-in (a redirect in `entraExternalId` mode; a no-op in development). */
  readonly signIn: (options?: SignInOptions) => void;
  /** Signs the user out (a redirect in `entraExternalId` mode; a no-op in development). */
  readonly signOut: () => void;
  /**
   * Resolves a bearer token for the API, or `undefined` when unauthenticated. Called per request by
   * the API client, so a token refreshed mid-session is picked up without rebuilding the client.
   */
  readonly getAccessToken: () => Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Provides authentication state and actions to the application.
 *
 * In `development` mode it short-circuits to the simulated identity with **no MSAL loaded**, so the
 * whole stack stays exercisable on Windows without an Entra tenant — the same reason the API keeps a
 * development header scheme. In `entraExternalId` mode it delegates to MSAL.
 *
 * @param props - Component props.
 * @param props.children - The application tree.
 * @returns The provider element.
 */
export function AuthProvider({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  const config = useMemo(() => readAuthConfig(), []);

  if (config.mode === 'development') {
    return <DevelopmentAuthProvider config={config}>{children}</DevelopmentAuthProvider>;
  }

  return <EntraAuthProvider config={config}>{children}</EntraAuthProvider>;
}

/**
 * The development short-circuit: always authenticated as the simulated identity, no token, no SDK.
 *
 * @param props - Component props.
 * @param props.config - The resolved configuration carrying the development identity.
 * @param props.children - The application tree.
 * @returns The provider element.
 */
function DevelopmentAuthProvider({
  config,
  children,
}: {
  readonly config: AuthConfig;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const value = useMemo<AuthContextValue>(
    () => ({
      status: 'authenticated',
      user: { name: config.developmentIdentity.name, subject: config.developmentIdentity.subject },
      signIn: () => undefined,
      signOut: () => undefined,
      getAccessToken: async () => undefined,
    }),
    [config],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Creates and initialises the MSAL instance, then hands off to {@link EntraAuthState}.
 *
 * MSAL v4 requires `initialize()` to complete before any interaction, so the tree renders a
 * `loading` context until it resolves rather than risking an "uninitialised" throw on first action.
 *
 * @param props - Component props.
 * @param props.config - The resolved configuration carrying the MSAL settings.
 * @param props.children - The application tree.
 * @returns The provider element.
 */
function EntraAuthProvider({
  config,
  children,
}: {
  readonly config: AuthConfig;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const instance = useMemo(() => new PublicClientApplication(toMsalConfiguration(config)), [config]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    void instance.initialize().then(() => {
      if (active) {
        setReady(true);
      }
    });
    return () => {
      active = false;
    };
  }, [instance]);

  if (!ready) {
    // Withhold the tree until MSAL is initialised, so no consumer ever sees a half-wired context
    // (a login button whose handler is still a no-op, say). The window is a single microtask.
    return (
      <Stack align="center" gap="lg">
        <Spinner label="Starting Greasemap" />
      </Stack>
    );
  }

  return (
    <MsalProvider instance={instance}>
      <EntraAuthState config={config}>{children}</EntraAuthState>
    </MsalProvider>
  );
}

/**
 * Derives the auth context from MSAL's account and interaction state.
 *
 * @param props - Component props.
 * @param props.config - The resolved configuration carrying the API scope.
 * @param props.children - The application tree.
 * @returns The provider element.
 */
function EntraAuthState({
  config,
  children,
}: {
  readonly config: AuthConfig;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const { instance, accounts, inProgress } = useMsal();
  const account: AccountInfo | null = accounts[0] ?? null;

  // The token accessor is handed to the API client and must keep a stable identity, so it reads the
  // current account from a ref rather than closing over a value that changes on every sign-in.
  const accountRef = useRef<AccountInfo | null>(account);
  accountRef.current = account;

  const scopes = useMemo(() => apiScopes(config), [config]);

  const getAccessToken = useCallback(async (): Promise<string | undefined> => {
    const current = accountRef.current;
    if (current === null) {
      return undefined;
    }

    try {
      const result = await instance.acquireTokenSilent({ account: current, scopes });
      return result.accessToken;
    } catch (error) {
      // A silent failure that needs the user (expired session, new consent) escalates to a redirect;
      // any other failure returns no token and lets the request fail as 401, which the UI handles.
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ account: current, scopes });
      }
      return undefined;
    }
  }, [instance, scopes]);

  const signIn = useCallback(
    (options?: SignInOptions) => {
      void instance.loginRedirect({
        scopes,
        // `prompt=create` starts Entra's hosted flow on its sign-up experience (the "Create account"
        // path) rather than sign-in; reset is a link Entra already renders on that page.
        ...(options?.createAccount === true ? { prompt: 'create' } : {}),
      });
    },
    [instance, scopes],
  );

  const signOut = useCallback(() => {
    void instance.logoutRedirect();
  }, [instance]);

  const status: AuthStatus =
    inProgress !== InteractionStatus.None ? 'loading' : account !== null ? 'authenticated' : 'unauthenticated';

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: account === null ? null : { name: account.name ?? account.username, subject: account.localAccountId },
      signIn,
      signOut,
      getAccessToken,
    }),
    [status, account, signIn, signOut, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Reads the authentication context.
 *
 * @returns The auth context.
 * @throws {Error} Called outside an {@link AuthProvider}.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error('useAuth must be used inside an AuthProvider.');
  }

  return context;
}
